import { reportClientError } from "./client-error.js";
let installed = false;
// Third-party browser extensions inject scripts that throw into our window.
const EXTENSION_FRAME_RE = /(?:chrome|moz|safari-web)-extension:\/\//;
const STACK_FRAME_RE = /:\d+:\d+\)?\s*$/;
// The browser's own notice that a resize callback changed layout again: the spec delivers the
// rest on the next frame, so nothing failed. Kept as an expected record, never a fault.
const RESIZE_OBSERVER_LOOP_RE = /^ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;
// It can fire every frame: once per page, so it never spends the client-log budget real faults need.
let resizeLoopReported = false;
/** True when every stack frame lives in a browser extension (and there is at least one). */
export function isExtensionOnlyStack(stack) {
    if (!stack)
        return false;
    const frames = stack.split("\n").filter((line) => STACK_FRAME_RE.test(line));
    return frames.length > 0 && frames.every((line) => EXTENSION_FRAME_RE.test(line));
}
function currentContext() {
    if (typeof window === "undefined")
        return {};
    const pathname = window.location?.pathname;
    // Never the query string or the hash: they can carry tokens and search terms.
    const origin = window.location?.origin;
    const href = origin && pathname ? `${origin}${pathname}` : undefined;
    const workspaceId = pathname?.match(/\/workspace\/([^/]+)/)?.[1] ?? null;
    return { pathname, href, workspaceId };
}
/**
 * Idempotent: wires window.onerror + unhandledrejection to reportClientError.
 * Safe to call multiple times (only the first install takes effect). No-op when
 * there's no window (SSR / non-browser).
 */
export function installGlobalErrorHandlers(options = {}) {
    if (installed)
        return;
    if (typeof window === "undefined")
        return;
    installed = true;
    window.addEventListener("error", (event) => {
        const err = event.error;
        if (isExtensionOnlyStack(err?.stack) || EXTENSION_FRAME_RE.test(event.filename ?? ""))
            return;
        const message = err?.message ?? event.message ?? "Uncaught error";
        const resizeLoop = RESIZE_OBSERVER_LOOP_RE.test(message);
        if (resizeLoop && resizeLoopReported)
            return;
        if (resizeLoop)
            resizeLoopReported = true;
        reportClientError({
            source: "window.onerror",
            message,
            ...(resizeLoop ? { failure_kind: "expected" } : {}),
            stack: err?.stack ?? null,
            platform: options.platform,
            version: options.version,
            ...currentContext(),
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
        });
    });
    window.addEventListener("unhandledrejection", (event) => {
        const reason = event.reason;
        const err = reason instanceof Error ? reason : undefined;
        if (isExtensionOnlyStack(err?.stack))
            return;
        reportClientError({
            source: "unhandledrejection",
            message: err?.message ?? String(reason),
            stack: err?.stack ?? null,
            platform: options.platform,
            version: options.version,
            ...currentContext(),
        });
    });
}
// Test-only: clear the install guard between tests.
export function __resetGlobalHandlersForTests() {
    installed = false;
    resizeLoopReported = false;
}
//# sourceMappingURL=global-handlers.js.map